import { useCallback } from 'react';
import { useSaveSwarmRulesMutation, useGetActiveSwarmRulesQuery } from '../../../../../../../../../services/autoBuyApi';
import { useNotification } from '../../../../../../../../../context/NotificationContext';
import { useEditOrCreateSwarmMutation } from '../../../../../../../../../services/swarmsApi';
import { AutoBuyParameter, ActiveSwarmAutoBuyRulesResponse } from '../types';
import {createErrorMessage} from '@/utils/index';

/**
 * Hook for handling the AutoBuy API interactions
 * @param swarmId - ID of the swarm to get/save rules for
 */
export const useAutoBuyApi = (swarmId?: string | number) => {
  const { showNotification } = useNotification();
  
  // RTK Query hooks
  const { data: rulesData, isLoading, error } = useGetActiveSwarmRulesQuery(swarmId, {
    skip: !swarmId
  });
  
  const [saveRules, { isLoading: isSaving }] = useSaveSwarmRulesMutation();
  const [editSwarm, { isLoading: isTogglingAutoBuy }] = useEditOrCreateSwarmMutation();

/**
   * Save rules to the API
   * @param rules - The rules to save
   * @param operationType - save | delete | update
   * @param showNotifications - whether to call showNotification (default: true)
   */
  const handleSaveRules = useCallback(
    async (
      rules: Omit<AutoBuyParameter, 'tempId'>[],
      operationType: 'save' | 'delete' | 'update' = 'save',
      showNotifications = true
    ) => {
      if (!swarmId) {
        if (showNotifications) {
          showNotification('Swarm ID is required to save rules', 'error')
        }
        return { success: false as const }
      }

      try {
        // Call mutation to save rules
        const result = await saveRules({ swarmId, rules }).unwrap()

        if (showNotifications) {
          // Show success notification
          let msg = 'AutoBuy rules saved successfully'
          if (operationType === 'delete') msg = 'AutoBuy rule deleted successfully'
          if (operationType === 'update') msg = 'AutoBuy rules updated successfully'
          showNotification(msg, 'success')
        }

        return { success: true as const, data: result }
      } catch (err: any) {
        console.error('Error saving AutoBuy rules:', err)
        if (showNotifications) {
          let errMsg = 'Failed to save AutoBuy rules'
          if (operationType === 'delete') errMsg = 'Failed to delete AutoBuy rule'
          if (operationType === 'update') errMsg = 'Failed to update AutoBuy rules'
          showNotification(errMsg, 'error')
        }
        return { success: false as const, error: err }
      }
    },[swarmId, saveRules, showNotification]
  )

  /**
   * Toggle auto buy status for the swarm.
   * @param isCurrentlyActive - current state
   * @param rulesPayload? - optional payload to save when activating AutoBuy
  */
  const handleToggleAutoBuys = useCallback(
    async (
      isCurrentlyActive: boolean,
      rulesPayload?: Omit<AutoBuyParameter, 'tempId'>[]
    ) => {
      if (!swarmId) {
        showNotification('Swarm ID is required to toggle auto buy status', 'error')
        return { success: false as const }
      }

      const willActivate = !isCurrentlyActive

      if (willActivate) {
        if (!rulesPayload) {
          console.error('No rules payload passed for activation')
          showNotification('Cannot activate AutoBuy: missing rules', 'error')
          return { success: false as const }
        }
        // guarda primero, sin notis internas
        const saveResult = await handleSaveRules(rulesPayload, 'save', false)
        if (!saveResult.success) {
          showNotification(
            saveResult.error?.message ?? 'Error preparing AutoBuy rules',
            'error'
          )
          return { success: false as const, error: saveResult.error }
        }
      }

      // Define the type with all possible properties
      let body: {
        id: string | number;
        isAutoBuyActive: boolean;
        running?: boolean; // Make this optional
      } = {
        id: swarmId,
        isAutoBuyActive: willActivate,
      }
      
      if(willActivate){
        body.running = true
      }

      try {
        const result = await editSwarm(body).unwrap()
        showNotification(
          `AutoBuy ${willActivate ? 'started' : 'stopped'} successfully`,
          'success'
        )
        return { success: true as const, data: result }
      } catch (err: any) {
        console.error('Error toggling AutoBuy status:', err)
        showNotification(createErrorMessage(err), 'error')
        return { success: false as const, error: err }
      }
    },
    [swarmId, editSwarm, handleSaveRules, showNotification]
  )

  return {
    rulesData,
    isLoading,
    isSaving,
    isTogglingAutoBuy,
    error,
    handleSaveRules,
    handleToggleAutoBuys
  };
};
